import { useAuth } from "@/hooks/use-auth";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { countBellItems } from "@/lib/bellCount";
import { Bell, LogOut, Settings, User, Phone, LifeBuoy, Video, Building2, Banknote, Calendar, Truck } from "lucide-react";
import laMirageLogo from "@assets/download.png";
import { Link } from "wouter";
import { useQuery } from "@tanstack/react-query";
import HamburgerNav from "./hamburger-nav";
import { useCommandPalette } from "@/hooks/use-command-palette";
import { getClientPaletteConfig, getAdminPaletteConfig } from "@/lib/palette-config";
import CommandPalette from "@/components/command-palette/CommandPalette";
import PaletteTrigger from "@/components/command-palette/PaletteTrigger";

export default function Header() {
  const { user, logout } = useAuth();

  // Fetch notifications count for the bell icon
  const { data: notifications = [] } = useQuery({
    queryKey: ['/api/notifications/client'],
    enabled: !!user,
    refetchInterval: 30000,
  });

  // Fetch active announcements count
  const { data: announcements = [] } = useQuery({
    queryKey: ['/api/announcements/active'],
    enabled: !!user,
    refetchInterval: 30000,
  });

  // Unread (non-read) notifications + active announcements — see lib/bellCount.ts
  const unreadCount = countBellItems(notifications, announcements);

  // Get user's weddings for client config
  const { data: weddings } = useQuery({
    queryKey: ['/api/weddings'],
    enabled: user?.role === 'client',
  });
  const primaryWeddingId = (weddings as any)?.[0]?.id;

  // Command palette config
  const isClient = user?.role === 'client';
  const config = isClient
    ? getClientPaletteConfig(primaryWeddingId)
    : getAdminPaletteConfig(user?.role || "admin");

  const palette = useCommandPalette(config, user?.role || "client");

  const getInitials = (firstName: string, lastName: string) => {
    return `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`.toUpperCase();
  };

  // Support Center S1: the prominent "Hub" slot now opens Support (CommHub is
  // retired quietly — its /comm-hub route still exists, it just lost the
  // prime placement). Staff → agent workspace, clients → help portal.
  const getSupportRoute = () => {
    if (user?.role === 'admin' || user?.role === 'event_manager' || user?.role === 'operations_manager') {
      return '/admin/support';
    }
    return '/support';
  };

  const getDashboardRoute = () => {
    switch (user?.role) {
      case 'admin': return '/admin';
      case 'event_manager': return '/';
      case 'operations_manager': return '/';
      case 'vendor': return '/vendor';
      case 'accountant': return '/accountant';
      default: return '/bookings';
    }
  };

  return (
    <header className="bg-gradient-to-r from-slate-950 via-gray-950 to-black shadow-2xl border-b border-amber-500/40 relative overflow-visible backdrop-blur-sm">
      <div className="w-full relative z-10">
        {/* Phones (<768px): the search / status trigger takes its own full-width second row. On one
            row it was squeezed to a sliver under the logo by the Meet/Support/bell/avatar buttons, so
            "24 overdue" (the rotating status) could not be read or tapped. md+ stays one 64px row. */}
        <div className="flex flex-wrap md:flex-nowrap justify-between items-center md:h-16 px-4 sm:px-6 lg:px-8">
          {/* Left-Aligned Premium Logo Section */}
          <div className="flex items-center space-x-4 flex-shrink-0 h-16">
            {/* Mobile: Show hamburger menu */}
            <div className="lg:hidden">
              <HamburgerNav />
            </div>

            {/* Desktop: Left-aligned luxury logo */}
            <Link href={getDashboardRoute()} className="hidden lg:flex items-center space-x-4 hover:scale-[1.01] transition-all duration-300 group ml-0">
              <div className="relative">
                <div className="absolute -inset-2 bg-gradient-to-r from-[#B8860B] via-amber-500 to-yellow-500 rounded-xl blur opacity-50 group-hover:opacity-70 transition-all duration-300"></div>
                <div className="relative bg-gradient-to-br from-[#B8860B]/20 via-amber-500/10 to-yellow-500/5 p-2.5 rounded-xl border border-[#B8860B]/30 backdrop-blur-sm shadow-xl">
                  <img
                    src={laMirageLogo}
                    alt="LA Mirage Logo"
                    className="w-9 h-9 object-contain filter drop-shadow-md"
                  />
                </div>
              </div>
              <div className="flex flex-col justify-center">
                <h1 className="font-playfair font-bold text-xl bg-gradient-to-r from-[#B8860B] via-amber-400 to-yellow-300 bg-clip-text text-transparent leading-tight">
                  LA Mirage
                </h1>
                <p className="text-[10px] text-amber-300/70 font-medium tracking-wide uppercase">
                  Premium Wedding Venue
                </p>
              </div>
            </Link>

            {/* Mobile: Compact logo only */}
            <div className="lg:hidden">
              <Link href={getDashboardRoute()} className="flex items-center hover:scale-[1.02] transition-all duration-300 group">
                <div className="relative">
                  <div className="absolute -inset-2 bg-gradient-to-r from-[#B8860B] to-amber-500 rounded-xl blur opacity-50 group-hover:opacity-70 transition-opacity"></div>
                  <div className="relative bg-gradient-to-br from-[#B8860B]/20 to-amber-500/10 p-2 rounded-xl border border-[#B8860B]/30 backdrop-blur-sm">
                    <img src={laMirageLogo} alt="LA Mirage" className="w-8 h-8 object-contain" />
                  </div>
                </div>
              </Link>
            </div>
          </div>

          {/* Command Palette Trigger — replaces old search bar */}
          <div data-testid="header-palette-row" className="order-last basis-full pb-3 md:order-none md:basis-auto md:pb-0 flex-1 min-w-0 flex justify-center md:mx-4 md:max-w-[400px]">
            <PaletteTrigger onClick={() => palette.setIsOpen(true)} />
          </div>

          {/* Enhanced Luxury User Menu */}
          <div className="flex items-center space-x-2 sm:space-x-3 h-16">
            {/* Events Button - Event Managers & Admin, Desktop Only */}
            {(user?.role === 'admin' || user?.role === 'event_manager' || user?.email === 'lijo@la-mirage.in') && (
              <Link
                href="/admin/events-hub"
                aria-label="Events" title="Events"
                className="hidden lg:flex items-center space-x-2 bg-gradient-to-r from-rose-600/15 to-rose-500/10 hover:from-rose-600/25 hover:to-rose-500/15 border border-rose-500/30 hover:border-rose-500/50 rounded-xl px-2.5 min-[1400px]:px-4 py-2 transition-all duration-300 group backdrop-blur-sm shadow-lg hover:shadow-xl"
              >
                <Calendar className="w-4 h-4 text-rose-500 group-hover:text-rose-400 transition-colors" />
                <span className="hidden min-[1400px]:inline text-sm font-semibold text-rose-500 group-hover:text-rose-400 transition-colors">
                  Events
                </span>
              </Link>
            )}

            {/* Finance Button - Event Managers & Admin, Desktop Only */}
            {(user?.role === 'admin' || user?.role === 'event_manager' || user?.role === 'operations_manager') && (
              <Link
                href="/admin/finance-hub"
                aria-label="Finance" title="Finance"
                className="hidden lg:flex items-center space-x-2 bg-gradient-to-r from-emerald-600/15 to-emerald-500/10 hover:from-emerald-600/25 hover:to-emerald-500/15 border border-emerald-500/30 hover:border-emerald-500/50 rounded-xl px-2.5 min-[1400px]:px-4 py-2 transition-all duration-300 group backdrop-blur-sm shadow-lg hover:shadow-xl"
              >
                <Banknote className="w-4 h-4 text-emerald-500 group-hover:text-emerald-400 transition-colors" />
                <span className="hidden min-[1400px]:inline text-sm font-semibold text-emerald-500 group-hover:text-emerald-400 transition-colors">
                  Finance
                </span>
              </Link>
            )}

            {/* Venue Management Button - Event Managers & Admin, Desktop Only */}
            {(user?.role === 'admin' || user?.role === 'event_manager' || user?.role === 'operations_manager' || user?.email === 'lijo@la-mirage.in') && (
              <Link
                href="/admin/venue-management"
                aria-label="Venue" title="Venue"
                className="hidden lg:flex items-center space-x-2 bg-gradient-to-r from-cyan-600/15 to-cyan-500/10 hover:from-cyan-600/25 hover:to-cyan-500/15 border border-cyan-500/30 hover:border-cyan-500/50 rounded-xl px-2.5 min-[1400px]:px-4 py-2 transition-all duration-300 group backdrop-blur-sm shadow-lg hover:shadow-xl"
              >
                <Building2 className="w-4 h-4 text-cyan-500 group-hover:text-cyan-400 transition-colors" />
                <span className="hidden min-[1400px]:inline text-sm font-semibold text-cyan-500 group-hover:text-cyan-400 transition-colors">
                  Venue
                </span>
              </Link>
            )}

            {/* Transport Hub Button - Event Managers & Admin, Desktop Only */}
            {(user?.role === 'admin' || user?.role === 'event_manager' || user?.role === 'operations_manager') && (
              <Link
                href="/admin/transport"
                aria-label="Transport" title="Transport"
                className="hidden lg:flex items-center space-x-2 bg-gradient-to-r from-sky-600/15 to-sky-500/10 hover:from-sky-600/25 hover:to-sky-500/15 border border-sky-500/30 hover:border-sky-500/50 rounded-xl px-2.5 min-[1400px]:px-4 py-2 transition-all duration-300 group backdrop-blur-sm shadow-lg hover:shadow-xl"
              >
                <Truck className="w-4 h-4 text-sky-500 group-hover:text-sky-400 transition-colors" />
                <span className="hidden min-[1400px]:inline text-sm font-semibold text-sky-500 group-hover:text-sky-400 transition-colors">
                  Transport
                </span>
              </Link>
            )}

            {/* Meetings Button - Desktop only (lg+) */}
            <Link
              href="/meetings"
              aria-label="Meet" title="Meet"
              className="hidden lg:flex items-center space-x-2 bg-gradient-to-r from-[#B8860B]/15 to-amber-500/10 hover:from-[#B8860B]/25 hover:to-amber-500/15 border border-[#B8860B]/30 hover:border-[#B8860B]/50 rounded-xl px-2.5 min-[1400px]:px-4 py-2 transition-all duration-300 group backdrop-blur-sm shadow-lg hover:shadow-xl"
            >
              <Video className="w-4 h-4 text-[#B8860B] group-hover:text-amber-400 transition-colors" />
              <span className="hidden min-[1400px]:inline text-sm font-semibold text-[#B8860B] group-hover:text-amber-400 transition-colors">
                Meet
              </span>
            </Link>

            {/* Meetings Button - Mobile icon only */}
            <Link
              href="/meetings"
              aria-label="Meetings"
              className="lg:hidden md:hidden flex items-center justify-center bg-gradient-to-r from-[#B8860B]/15 to-amber-500/10 hover:from-[#B8860B]/25 hover:to-amber-500/15 border border-[#B8860B]/30 hover:border-[#B8860B]/50 rounded-xl p-2 transition-all duration-300 group backdrop-blur-sm shadow-lg hover:shadow-xl"
            >
              <Video className="w-4 h-4 text-[#B8860B] group-hover:text-amber-400 transition-colors" />
            </Link>

            {/* Support Button (took over the old Comm Hub slot) - Desktop only (lg+) */}
            <Link
              href={getSupportRoute()}
              aria-label="Support" title="Support"
              className="hidden lg:flex items-center space-x-2 bg-gradient-to-r from-[#B8860B]/15 to-amber-500/10 hover:from-[#B8860B]/25 hover:to-amber-500/15 border border-[#B8860B]/30 hover:border-[#B8860B]/50 rounded-xl px-2.5 min-[1400px]:px-4 py-2 transition-all duration-300 group backdrop-blur-sm shadow-lg hover:shadow-xl"
            >
              <LifeBuoy className="w-4 h-4 text-[#B8860B] group-hover:text-amber-400 transition-colors" />
              <span className="hidden min-[1400px]:inline text-sm font-semibold text-[#B8860B] group-hover:text-amber-400 transition-colors">
                Support
              </span>
            </Link>

            {/* Support - Mobile icon only */}
            <Link
              href={getSupportRoute()}
              aria-label="Support"
              className="lg:hidden md:hidden flex items-center justify-center bg-gradient-to-r from-[#B8860B]/15 to-amber-500/10 hover:from-[#B8860B]/25 hover:to-amber-500/15 border border-[#B8860B]/30 hover:border-[#B8860B]/50 rounded-xl p-2 transition-all duration-300 group backdrop-blur-sm shadow-lg hover:shadow-xl"
            >
              <LifeBuoy className="w-4 h-4 text-[#B8860B] group-hover:text-amber-400 transition-colors" />
            </Link>

            {/* WhatsApp Button - Event Managers & Admin (lijo), Desktop */}
            {(user?.role === 'event_manager' || user?.email === 'lijo@la-mirage.in') && (
              <Link
                href="/admin/live-chat-enterprise"
                aria-label="WhatsApp" title="WhatsApp"
                className="hidden md:flex items-center space-x-2 bg-gradient-to-r from-green-600/15 to-green-500/10 hover:from-green-600/25 hover:to-green-500/15 border border-green-500/30 hover:border-green-500/50 rounded-xl px-2.5 min-[1400px]:px-4 py-2 transition-all duration-300 group backdrop-blur-sm shadow-lg hover:shadow-xl"
              >
                <svg className="w-4 h-4 text-green-500 group-hover:text-green-400 transition-colors" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                <span className="hidden min-[1400px]:inline text-sm font-semibold text-green-500 group-hover:text-green-400 transition-colors">
                  WAB
                </span>
              </Link>
            )}

            {/* Voice Call Button - Event Managers & Admin (lijo), Desktop */}
            {(user?.role === 'event_manager' || user?.email === 'lijo@la-mirage.in') && (
              <Link
                href="/admin/callerdesk"
                aria-label="Calls" title="Calls"
                className="hidden md:flex items-center space-x-2 bg-gradient-to-r from-blue-600/15 to-blue-500/10 hover:from-blue-600/25 hover:to-blue-500/15 border border-blue-500/30 hover:border-blue-500/50 rounded-xl px-2.5 min-[1400px]:px-4 py-2 transition-all duration-300 group backdrop-blur-sm shadow-lg hover:shadow-xl"
              >
                <Phone className="w-4 h-4 text-blue-500 group-hover:text-blue-400 transition-colors" />
                <span className="hidden min-[1400px]:inline text-sm font-semibold text-blue-500 group-hover:text-blue-400 transition-colors">
                  Calls
                </span>
              </Link>
            )}

            {/* Notifications */}
            {['client', 'admin', 'event_manager', 'operations_manager'].includes(user?.role || '') && (
              <Link
                href="/notifications"
                aria-label="Notifications"
                className="relative flex items-center justify-center bg-gradient-to-r from-[#B8860B]/15 to-amber-500/10 hover:from-[#B8860B]/25 hover:to-amber-500/15 border border-[#B8860B]/30 hover:border-[#B8860B]/50 rounded-xl p-2 transition-all duration-300 group backdrop-blur-sm shadow-lg hover:shadow-xl"
              >
                <Bell className="w-5 h-5 text-[#B8860B] group-hover:text-amber-400 transition-colors" />
                {unreadCount > 0 && (
                  <Badge className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs bg-red-500 text-white border-0 rounded-full">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </Badge>
                )}
              </Link>
            )}

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" aria-label="Account menu" className="flex items-center space-x-3 text-white hover:text-[#B8860B] hover:bg-gradient-to-r hover:from-[#B8860B]/10 hover:to-amber-500/5 transition-all duration-300 px-3 py-2 rounded-xl group">
                  <Avatar className="h-8 w-8 ring-2 ring-[#B8860B]/30 group-hover:ring-[#B8860B]/50 transition-all duration-300">
                    <AvatarImage src={`https://api.dicebear.com/7.x/initials/svg?seed=${user?.firstName} ${user?.lastName}`} />
                    <AvatarFallback className="bg-gradient-to-r from-[#B8860B] to-amber-500 text-black font-bold text-sm">{getInitials(user?.firstName || '', user?.lastName || '')}</AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className="w-64 bg-gradient-to-b from-gray-900 to-black border border-[#B8860B]/30 shadow-2xl backdrop-blur-sm"
                style={{ backgroundColor: 'rgba(17, 24, 39, 0.95)', borderColor: 'rgba(184, 134, 11, 0.3)' }}
              >
                <DropdownMenuItem asChild className="text-white hover:bg-gradient-to-r hover:from-[#B8860B]/10 hover:to-amber-500/5 focus:bg-gradient-to-r focus:from-[#B8860B]/10 focus:to-amber-500/5 cursor-pointer p-4 transition-all duration-300">
                  <Link href="/profile">
                    <User className="mr-3 h-5 w-5 text-[#B8860B]" />
                    <div>
                      <span className="text-white font-medium">Profile</span>
                      <p className="text-xs text-gray-400">Manage your account</p>
                    </div>
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild className="text-white hover:bg-gradient-to-r hover:from-[#B8860B]/10 hover:to-amber-500/5 focus:bg-gradient-to-r focus:from-[#B8860B]/10 focus:to-amber-500/5 cursor-pointer p-4 transition-all duration-300">
                  <Link href="/settings">
                    <Settings className="mr-3 h-5 w-5 text-[#B8860B]" />
                    <div>
                      <span className="text-white font-medium">Settings</span>
                      <p className="text-xs text-gray-400">Preferences & config</p>
                    </div>
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={logout} className="text-white hover:bg-gradient-to-r hover:from-red-500/10 hover:to-red-600/5 focus:bg-gradient-to-r focus:from-red-500/10 focus:to-red-600/5 cursor-pointer p-4 transition-all duration-300">
                  <LogOut className="mr-3 h-5 w-5 text-red-400" />
                  <div>
                    <span className="text-white font-medium">Logout</span>
                    <p className="text-xs text-gray-400">Sign out of account</p>
                  </div>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      {/* Command Palette Modal */}
      <CommandPalette
        isOpen={palette.isOpen}
        query={palette.query}
        results={palette.results}
        selectedIndex={palette.selectedIndex}
        isLoading={palette.isLoading}
        isAILoading={palette.isAILoading}
        aiAnswer={palette.aiAnswer}
        aiAction={palette.aiAction}
        inputRef={palette.inputRef}
        config={config}
        onQueryChange={palette.onQueryChange}
        onKeyDown={palette.onKeyDown}
        onSelect={palette.executeItem}
        onHover={palette.setSelectedIndex}
        onClose={palette.close}
      />
    </header>
  );
}
